import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { MatMenuModule, MatMenuTrigger } from "@angular/material/menu";
import { SharedModule } from "../../../../shared/shared.module";
import { ExpandIndicatorComponent } from "../../../../../common/components/expand.indicator/expand.indicator.component";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ImageLinkFileSelectorComponent } from "../../../../../common/components/image.link.file.selector/image.link.file.selector.component";
import { ButtonTriggerEvent } from "../../../../../common/components/button/button.component";
import { catchError, of, switchMap, takeUntil, tap } from "rxjs";
import { BaseComponent } from "../../../../../common/components/base.component";
import { ActivatedRoute } from "@angular/router";
import { ErrorAlertService } from "../../../../../common/services/error.alert/error.alert.service";
import { TripPlannerService } from "../../../services/trip.planner.service";
import { CreateItineraryCommand, Itinerary } from "../../../models/itinerary";
import { finalize } from "rxjs/operators";
import { CommonModule, NgForOf } from "@angular/common";
import { MatButtonToggleChange, MatButtonToggleModule } from "@angular/material/button-toggle";
import { TravelMode } from "../../../../shared/services/google.map.service";
import { TabImageLinkFileSelectorComponent } from "../../../../../common/components/tab.image.link.file.selector/tab.image.link.file.selector.component";
import { IDateRange } from "src/app/common/components/date.range.picker/date.range";
import { EventTimeService } from "src/app/modules/event-calendar/services/event.time.service";
import { DateTimePickerComponent } from "src/app/common/components/date.time.picker/date.time.picker.component";

// @ts-ignore
@Component({
  selector: "ig-create-itinerary",
  standalone: true,
  imports: [
    MatMenuModule,
    SharedModule,
    ExpandIndicatorComponent,
    NgForOf,
    ReactiveFormsModule,
    MatButtonToggleModule,
    FormsModule,
    CommonModule,
    TabImageLinkFileSelectorComponent,
  ],
  templateUrl: "./create.itinerary.component.html",
  styleUrl: "./create.itinerary.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class CreateItineraryComponent extends BaseComponent implements OnInit {
  @HostBinding("class") hostClass = "ig-create-itinerary";

  @Input() triggerText = "Create a new itinerary";
  @Input() menuClass = "";
  @Input() order = 0;
  @Input() itinerary: Itinerary = null;

  @Input() customFieldControls: FormArray;
  @Input() endFormControl: FormControl;

  @ViewChild("imgUploader", { static: false })
  imgUploader: TabImageLinkFileSelectorComponent;
  @ViewChild("releaseDatePicker", { static: false })
  releaseDatePicker: DateTimePickerComponent;

  @Output() onCreateItinerary = new EventEmitter<any>();

  form: FormGroup;
  imageUrl: string;
  submitBtnTrigger: { trigger: MatMenuTrigger; event: ButtonTriggerEvent } = null;

  constructor(
    public activatedRoute: ActivatedRoute,
    public timeService: EventTimeService,
    private _tripService: TripPlannerService,
    private _fb: FormBuilder,
    private _errorAlertService: ErrorAlertService
  ) {
    super({ activatedRoute });

    this.form = this._fb.group({
      title: [null, [Validators.required]],
      description: [null, []],
      imageLink: [null, []],
      isActive: [null, []],
      customFields: this._fb.array([
        this._fb.group({
          field: [null, []],
          // value: [null, []],
        }),
      ]),
      releaseDate: [null, []],
      expiryDate: [null, []],
    });
  }

  get isFormInValid() {
    return this.form.invalid;
  }

  get formArray() {
    return (this.form.get("customFields") as FormArray).controls;
  }

  onImageUploaded($event) {
    this.imageUrl = $event;
    this.itinerary && (this.itinerary.image = this.imageUrl);

    this.submit();
  }

  clearDateRange() {
    this.releaseDatePicker?.clear();
  }

  submit(event?: ButtonTriggerEvent, trigger?: MatMenuTrigger) {
    /**
     * changed the submit logic
     * originally, there is a submit image button below the image selector
     * now, that button is removed, only use the update button, so first check if image is changed and upload it, then in the callback, submit the itinerary changes
     */
    if (this.imgUploader.waitingUpload()) {
      if (!this.imgUploader.hasCopyrights) {
        this.imgUploader.showCopyRightsAlert();
        event?.immediateComplete();
        return;
      }
      this.imgUploader.submit(event);
      this.submitBtnTrigger = {
        event,
        trigger,
      };
      return;
    }

    const releaseDate = this.form.get("releaseDate").value;
    const expiryDate = this.form.get("expiryDate").value;
    if (releaseDate && expiryDate && releaseDate >= expiryDate) {
      this._errorAlertService.showErrorAlert("Release date must be before expiry date.");
      event?.immediateComplete();
      this.submitBtnTrigger?.event?.immediateComplete();
      return;
    }

    let widgetCode = this.activatedRoute.snapshot.params["widgetCode"];
    if (!widgetCode || widgetCode.toLowerCase() === "edit") {
      widgetCode = "default";
    }

    const command: CreateItineraryCommand = {
      hubName: this.hubName,
      title: this.form.get("title").value,
      description: this.form.get("description").value,
      imageLink: this.imageUrl || this.itinerary?.image,
      order: this.order,
      isActive: this.itinerary ? this.itinerary.isActive : false,
      customFields: this.getCustomFields(),
      detailsStyle: this.itinerary ? this.itinerary.detailsStyle : "map",
      mapStyle: this.itinerary ? this.itinerary.mapStyle : "dark",
      defaultTravelMode: this.itinerary
        ? this.itinerary.defaultTravelMode
        : {
            canChange: true,
            mode: "DRIVING",
          },
      releaseDate: releaseDate,
      expiryDate: expiryDate,
      widgetCodes: this.itinerary?.widgetCodes || [widgetCode],
    };

    const request = this.itinerary
      ? this._tripService.updateItinerary(this.itinerary._id, command)
      : this._tripService.createItinerary(command);

    request
      .pipe(
        takeUntil(this.isDestroyed),
        finalize(() => {
          event?.complete();
          this.submitBtnTrigger?.event?.complete();
        }),
        tap(() => {
          event?.success();
          this.submitBtnTrigger?.event?.success();
          if (this.itinerary) {
            this.itinerary.title = this.form.get("title").value;
            this.itinerary.description = this.form.get("description").value;
            this.itinerary.image = this.form.get("imageLink").value || this.itinerary.image;
            this.itinerary.customFields = this.getCustomFields();
            setTimeout(() => {
              trigger && trigger.closeMenu();
              this.submitBtnTrigger?.trigger?.closeMenu();
              this.submitBtnTrigger = null;
            }, 4000);
          } else {
            setTimeout(() => {
              trigger && trigger.closeMenu();
              this.submitBtnTrigger?.trigger?.closeMenu();
              this.form.get("title").setValue("");
              this.form.get("title").markAsUntouched();
              this.form.get("description").setValue("");
              this.form.get("imageLink").setValue("");
              this.imgUploader?.reset();
              this.submitBtnTrigger = null;
              this.clearDateRange();
            }, 5000);
          }
          this.onCreateItinerary.emit(this.itinerary);
        }),
        catchError((err) => {
          this._errorAlertService.showErrorAlert(err.message);
          return of(null);
        })
      )
      .subscribe();
  }

  ngOnInit(): void {
    // edit itinerary
    if (this.itinerary) {
      this.form.get("title").setValue(this.itinerary.title);
      this.form.get("description").setValue(this.itinerary.description);

      if (this.itinerary.customFields?.length) {
        this.removeFields(0);
        for (const field of this.itinerary.customFields) {
          this.addFields(field);
        }
      }

      setTimeout(() => {
        this.releaseDatePicker?.setTime(
          this.itinerary.releaseDate,
          this.frontendSettings.customSettings.timezoneFormatter,
          "start"
        );
        this.releaseDatePicker?.setTime(
          this.itinerary.expiryDate,
          this.frontendSettings.customSettings.timezoneFormatter,
          "end"
        );
      }, 1000);
    }
  }

  onFocus() {
    // this.setLabelWidth();
  }

  publishChange($event: MatButtonToggleChange) {
    this.itinerary.isActive = $event.value === "true";
  }

  addFields(data?: { field: string; value?: string }) {
    (this.form.get("customFields") as FormArray).controls.push(
      this._fb.group({
        field: [data?.field, [Validators.required]],
        // value: [data?.value, [Validators.required]],
      })
    );
  }

  removeFields(i: number) {
    (this.form.get("customFields") as FormArray).controls.splice(i, 1);
  }

  getCustomFields() {
    const controls = this.form.get("customFields")["controls"];
    const customFields = [];
    for (const time of controls) {
      // if (time.get('field').value?.trim() && time.get('value').value?.trim()) {
      if (time.get("field").value?.trim()) {
        customFields.push({
          field: time.get("field").value.trim(),
          // value: time.get('value').value.trim(),
        });
      }
    }
    return customFields;
  }

  isCustomFieldsInValid() {
    // const controls = this.form.get('customFields')['controls'];
    // return controls.any(x => !x.valid);
    return false;
  }
}
